import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { connect } from "react-redux";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Grid from "@material-ui/core/Grid";
import Divider from "@material-ui/core/Divider";

import axiosClient from "../../api";
import { useRoomRejoin } from "util/useRoomRejoin";
import { useMessagePolling } from "util/useMessagePolling";
import AQChatHeader from "view/AskQuestion/components/AQChatHeader";
import AQChatBody from "view/AskQuestion/components/AQChatBody";
import AQChatFooter from "view/AskQuestion/components/AQChatFooter";
import { Avatar, Badge, Stack, styled } from "@mui/material";
import { getFileLink } from "util/s3";
import { toastify, toastType } from "util/toastify";
import trade_favicon_transparent_bg from "../../assets/images/trade_favicon_transparent_bg.png";

const useStyles = makeStyles({
  table: {
    minWidth: 650,
  },
  chatSection: {
    display: "block",
    width: "100%",
    maxWidth: "1065px",
    marginLeft: "auto",
    marginRight: "auto",
    marginTop: "50px",
    marginBottom: "100px",
  },
  headBG: {
    backgroundColor: "#e0e0e0",
  },
  borderRight500: {
    borderRight: "1px solid #e0e0e0",
  },
  messageArea: {
    height: "50vh",
    overflowY: "auto",
  },
});

const StyledBadge = styled(Badge)(({ theme }) => ({
  "& .MuiBadge-badge": {
    // backgroundColor: "#1CC400",
    // color: "#1CC400",
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    "&::after": {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      borderRadius: "50%",
      border: "1px solid currentColor",
      content: '""',
    },
  },
}));

const sameRoom = (a, b) => a != null && b != null && String(a) === String(b);

// Keep ?roomId= in the address bar WITHOUT going through react-router. The
// router wraps every route in a CSSTransition keyed on location.key, so a
// history.replace() here remounts the whole page: the list blanks to "No
// history yet", refetches, and fades back in on every click. Writing the
// URL directly leaves the router's location untouched and the page in place.
// (Navigation from outside, like the bell, still goes through the router and
// is picked up by the requestedRoomId effect below.)
const syncRoomUrl = (id) => {
  try {
    window.history.replaceState(
      window.history.state,
      "",
      `/chat-history?roomId=${id}`
    );
  } catch (_) {
    // Nothing to do: the room is already selected in state.
  }
};

// The Messages page: every general ("Ask a question") conversation down the
// side, the selected one on the right. The selected conversation lives in
// the URL (?roomId=) so the bell, the "New Message Received" email and the
// reminder emails all land here with the right chat open, and so opening a
// second notification while already on this page switches conversation
// instead of silently staying on the old one.
const ChatHistory = ({ auth, socket }) => {
  const classes = useStyles();
  const location = useLocation();

  const requestedRoomId = useMemo(
    () => new URLSearchParams(location.search).get("roomId"),
    [location.search]
  );

  const [roomId, setRoomId] = useState(requestedRoomId);
  const [roomList, setRoomList] = useState([]);
  const [listLoaded, setListLoaded] = useState(false);
  const [, setRoomInfo] = useState(null);
  const [messages, setMessages] = useState([]);
  const [oponents, setOponents] = useState([]);
  // Whether the logged-in member's own account is paused, as the server
  // sees it right now (the redux copy is a login-time snapshot).
  const [viewerPaused, setViewerPaused] = useState(false);

  // Stay in the open room if the socket reconnects; the join below only runs
  // when the room is picked.
  useRoomRejoin(socket, "AQChatJoin", roomId);
  // Catch anything the socket didn't deliver (see util/useMessagePolling.js).
  useMessagePolling(roomId, messages, setMessages);

  // Follow the URL: a notification click while this page is open changes
  // only the query string.
  useEffect(() => {
    if (requestedRoomId) setRoomId(requestedRoomId);
  }, [requestedRoomId]);

  const selectRoom = (id) => {
    if (sameRoom(id, roomId)) return;
    setRoomId(id);
    syncRoomUrl(id);
  };

  useEffect(() => {
    auth.user.userId &&
      axiosClient
        .post("api/chat/get-all-history", { userId: auth.user.userId })
        .then(async (res) => {
          let tmp = res.data.data.rooms;
          for (let i = 0; i < tmp.length; i++) {
            if (tmp[i].user && tmp[i].user.media) {
              tmp[i].user.media.url = await getFileLink(tmp[i].user.media.url)
            }
          }
          setRoomList(tmp);
          setListLoaded(true);
        })
        .catch((error) => console.error(error));
  }, [auth.user.userId]);

  // No conversation asked for: open the most recent one.
  useEffect(() => {
    if (!requestedRoomId && !roomId && listLoaded && roomList.length > 0) {
      setRoomId(roomList[0].roomId);
      syncRoomUrl(roomList[0].roomId);
    }
  }, [requestedRoomId, roomId, listLoaded, roomList]);

  useEffect(() => {
    if (roomId) {
      axiosClient
        .post("api/chat/aq-chatroom-data", { roomId })
        .then(async (res) => {
          const room = res.data.data.room;
          if (!room) return;
          setRoomInfo(room);
          setMessages(room.messages);
          let tmp = room.members.filter(
            (member) => member.id !== auth.user.userId
          );
          setOponents(tmp);
          const me = room.members.find(
            (member) => member.id === auth.user.userId
          );
          setViewerPaused(Boolean(me && me.isPaused));

          socket.emit("AQChatJoin", {
            roomId,
          });

          // A conversation that hasn't had a message yet (just opened from a
          // profile) isn't in the history list. Show it at the top so the
          // page doesn't look like it opened nothing.
          if (tmp[0]) {
            const other = { ...tmp[0] };
            if (other.media && other.media.url) {
              other.media = {
                ...other.media,
                url: await getFileLink(other.media.url),
              };
            }
            setRoomList((list) =>
              list.some((item) => sameRoom(item.roomId, roomId))
                ? list
                : [{ roomId: room.id, user: other }, ...list]
            );
          }
        })
        .catch((error) => console.error(error));
    }

    return () => {
      socket.emit("AQChatDisconnect", {
        roomId,
      });
    };
  }, [auth.user.userId, roomId, socket]);

  // The server refuses a message (paused sender or receiver) by telling the
  // sender why instead of delivering it; surface that here.
  useEffect(() => {
    if (!socket) return undefined;
    const onInvalid = (reason) => {
      if (reason) toastify(String(reason), toastType.warning);
    };
    socket.on("InvalidAction", onInvalid);
    return () => socket.off("InvalidAction", onInvalid);
  }, [socket]);

  // A message from the other person is proof they're here: flip their dot to
  // green without waiting for a reload.
  useEffect(() => {
    if (!socket || !roomId) return undefined;
    const onMessage = (data) => {
      if (!data || !sameRoom(data.roomId, roomId)) return;
      if (data.senderId === auth.user.userId) return;
      const now = new Date().toISOString();
      setOponents((list) =>
        list.map((member) =>
          member.id === data.senderId ? { ...member, updatedAt: now } : member
        )
      );
      setRoomList((list) =>
        list.map((item) =>
          item.user && item.user.id === data.senderId
            ? { ...item, user: { ...item.user, updatedAt: now } }
            : item
        )
      );
    };
    socket.on("AQChatMessageResponse", onMessage);
    return () => socket.off("AQChatMessageResponse", onMessage);
  }, [socket, roomId, auth.user.userId]);

  return (
    <main className="flex-1 chat-history px-4">
      <Grid container component={Paper} className={classes.chatSection}>
        <div className="border border-solid rounded-md">
          <div className="grid grid-cols-12">
            <div className="col-span-4 sm:col-span-3 border-y-0 border-l-0 border-r border-solid">
              {roomList.length > 0 ? (
                roomList.map((item, index) => (
                  <React.Fragment key={`chat-room-${item.roomId}-${index}`}>
                    {item.user && (
                      <div
                        onClick={() => selectRoom(item.roomId)}
                        className={
                          "flex pl-3 items-center border-b border-solid border-t-0 border-x-0 border-gray-200 " +
                          (sameRoom(item.roomId, roomId) ? "bg-[#f4fbf3] " : "") +
                          // A paused member has stepped away: the conversation
                          // stays readable, but they are greyed out and their
                          // profile is no longer a click away from here.
                          (item.user.isPaused ? "opacity-50" : "")
                        }
                      >
                        <Stack
                          direction="row"
                          spacing={2}
                          className="justify-center mt-6 mb-3"
                        >
                          <StyledBadge
                            overlap="circular"
                            anchorOrigin={{
                              vertical: "bottom",
                              horizontal: "right",
                            }}
                            variant="dot"
                            color={
                              item.user.isPaused
                                ? "default"
                                : (new Date() - new Date(item.user.updatedAt)) /
                                  (1000 * 60) <
                                  5
                                  ? "primary"
                                  : "warning"
                            }
                          >
                            {item.user.isPaused ? (
                              <Avatar
                                alt={`${item.user.firstName} ${item.user.lastName}`}
                                src={`${item.user.media ? item.user.media.url : ""
                                  }` || trade_favicon_transparent_bg || ''}
                                className="grayscale"
                              />
                            ) : (
                              <Link to={`/user-profile/${item.user.id}`}>
                                <Avatar
                                  alt={`${item.user.firstName} ${item.user.lastName}`}
                                  src={`${item.user.media ? item.user.media.url : ""
                                    }` || trade_favicon_transparent_bg || ''}
                                />
                              </Link>
                            )}
                          </StyledBadge>
                        </Stack>
                        <div className="ml-3 mt-[5px] text-start">
                          <div className="flex items-end cursor-pointer max-sm:text-sm">
                            {`${item.user.firstName} ${item.user.lastName}`}
                          </div>
                          {item.user.isPaused && (
                            <div className="text-[11px] text-[#999] max-sm:text-[10px]">
                              Not currently available
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </React.Fragment>
                ))
              ) : (
                <p className="py-3 px-2">No history yet.</p>
              )}
            </div>
            <div className="col-span-8 sm:col-span-9">
              {oponents.length > 0 && <AQChatHeader oponents={oponents} />}
              <Divider />
              <AQChatBody
                classes={classes}
                socket={socket}
                roomId={roomId}
                messages={messages}
                setMessages={setMessages}
              />
              <Divider />
              <AQChatFooter
                socket={socket}
                roomId={roomId}
                oponents={oponents}
                viewerPaused={viewerPaused}
                messages={messages}
                setMessages={setMessages}
              />
            </div>
          </div>
        </div>
      </Grid>
    </main>
  );
};

const mapStateToProps = (state) => ({
  auth: state.auth,
});

export default connect(mapStateToProps, {})(ChatHistory);
